import React from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { css } from '@codemirror/lang-css';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';
import { getFormValues, getCanvasElement } from './utils/easyblocks';
import { postEasyblocksFormChange } from './utils/post-message';

export const injectGlobalCSS = (cssValue) => {
  const canvasElement = getCanvasElement();
  // If no canvas element, use the main document
  const targetDocument = canvasElement?.contentDocument || document;
  const targetBody = targetDocument.body;

  const existingStyle = targetDocument.querySelector(
    'style[data-widget-id="SwellCSS"]',
  );

  // Remove the style element if CSS is empty, null, or undefined
  if (!cssValue || cssValue.trim() === '') {
    if (existingStyle) {
      existingStyle.remove();
    }
    return;
  }

  if (existingStyle) {
    existingStyle.textContent = cssValue;
  } else {
    const styleElement = targetDocument.createElement('style');
    styleElement.type = 'text/css';
    styleElement.setAttribute('data-widget-id', 'SwellCSS');
    styleElement.textContent = cssValue;
    targetBody.appendChild(styleElement);
  }
};

export default class GlobalCSSWidget extends React.PureComponent {
  static contextType = EasyblocksBackendContext;

  cssValueRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      initialCssValue: '',
      isEditing: false,
    };
  }

  componentDidMount() {
    const { editorConfig } = this.context;
    const formValues = getFormValues();

    let platformCss = '';
    if (formValues?.custom_css) {
      // Use saved settings if they exist
      if (Array.isArray(formValues.custom_css)) {
        platformCss = formValues.custom_css.join('\n');
      } else if (typeof formValues.custom_css === 'object') {
        // Extract the CSS value from the expected structure
        const cssValue = formValues.custom_css.value;
        platformCss = cssValue || '';
      } else if (typeof formValues.custom_css === 'string') {
        platformCss = formValues.custom_css;
      }
    } else if (editorConfig?.globals?.settings?.custom_css) {
      // Only use preset if no saved settings exist
      if (Array.isArray(editorConfig.globals.settings.custom_css)) {
        platformCss = editorConfig.globals.settings.custom_css.join('\n');
      } else if (typeof editorConfig.globals.settings.custom_css === 'object') {
        platformCss = editorConfig.globals.settings.custom_css.value || '';
      } else {
        platformCss = editorConfig.globals.settings.custom_css;
      }
    }

    this.cssValueRef.current = platformCss;
    this.setState({ initialCssValue: platformCss });

    if (platformCss) {
      injectGlobalCSS(platformCss);

      postEasyblocksFormChange('custom_css', {
        value: platformCss,
        widgetId: 'SwellCSS',
      });
    }
  }

  componentWillUnmount() {
    const { editorConfig } = this.context;

    const cssValue = this.cssValueRef.current;
    if (
      (cssValue && cssValue.trim() !== '') ||
      editorConfig?.globals?.settings?.custom_css
    ) {
      injectGlobalCSS(cssValue);
    }
  }

  handleCssChange = (cssValue) => {
    const { isEditing } = this.state;

    if (!isEditing) {
      this.setState({ isEditing: true });
    }

    this.cssValueRef.current = cssValue;

    postEasyblocksFormChange('custom_css', {
      value: cssValue,
      widgetId: 'SwellCSS',
    });

    injectGlobalCSS(cssValue);
  };

  render() {
    const { initialCssValue } = this.state;

    return (
      <div>
        <details>
          <summary>
            <div className="fields-builder__FieldsGroupLabel custom-css-widget">
              <span>Global CSS</span>
              <svg
                className="chevron"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.35355 10.0606L8.70711 9.70709L11.7071 6.70709L11 5.99998L8.35355 8.64643L5.70711 5.99998L5 6.70709L8 9.70709L8.35355 10.0606Z"
                  fill="black"
                />
              </svg>
            </div>
          </summary>
          <div className="wrapFieldWithMeta__FieldWrapper custom-css-editor">
            <CodeMirror
              value={initialCssValue}
              height="200px"
              extensions={[css()]}
              onChange={this.handleCssChange}
            />
          </div>
        </details>
      </div>
    );
  }
}

GlobalCSSWidget.contextType = EasyblocksBackendContext;
